import { UserFeatureFlag, UserProfileRole } from '@@types/api.types';
import logoImg from '@assets/logos/az-iso-white.png';
import logoText from '@assets/logos/az-text.svg';
import { OrganizationSwitcher } from '@components/shared/organization-switcher.component';
import UserComponent from '@components/shared/user.component';
import { Avatar, AvatarImage } from '@components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from '@components/ui/sidebar';
import { useIsMobile } from '@hooks/mobile.hook';
import { cn } from '@lib/utils';
import { useTechnologiesListQueryOptions } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { useSidebarStore } from '@stores/sidebar.store';
import { useQuery } from '@tanstack/react-query';
import { canAccessMeiaCampaigns, canAccessMeiaStudioV1 } from '@utils/meia-access.util';
import {
  BriefcaseIcon,
  Building,
  Building2Icon,
  ChevronRight,
  CpuIcon,
  FileSearchIcon,
  FlagIcon,
  FolderIcon,
  FolderTreeIcon,
  GlobeIcon,
  House,
  LineChartIcon,
  ListChecksIcon,
  MessageSquareIcon,
  Package,
  RadarIcon,
  ServerCogIcon,
  SparklesIcon,
  TargetIcon,
  UserIcon,
  Users2Icon,
} from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

export const AppSidebar: React.FC = () => {
  const currentPath = useLocation().pathname;
  const { technologyId: currentTechnologyId } = useParams<{ technologyId?: string }>();
  const { data: user, isSelectedCompanyParentOrganization } = useGetCurrentUser();

  const { open, setOpen, state } = useSidebar();
  const isMobile = useIsMobile();

  const technologiesListQueryOptions = useTechnologiesListQueryOptions();
  const { data: technologies = [] } = useQuery(technologiesListQueryOptions);

  const {
    addRecentTechnology,
    recentTechnologiesCollapsed,
    recentTechnologyIds,
    removeRecentTechnology,
    setRecentTechnologiesCollapsed,
  } = useSidebarStore();

  const recentTechnologies = useMemo(() => {
    const MAX_TECHNOLOGIES = 5;

    const validTechnologies = recentTechnologyIds
      .map((id) => technologies.find((tech) => tech.id === id))
      .filter((tech): tech is NonNullable<typeof tech> => tech !== undefined);

    const invalidIds = recentTechnologyIds.filter((id) => !technologies.find((tech) => tech.id === id));
    invalidIds.forEach((id) => removeRecentTechnology(id));

    if (validTechnologies.length >= MAX_TECHNOLOGIES) {
      return validTechnologies.slice(0, MAX_TECHNOLOGIES);
    }

    const visitedIds = new Set(validTechnologies.map((tech) => tech.id));
    const fillTechnologies = technologies
      .filter((tech) => !visitedIds.has(tech.id))
      .slice(0, MAX_TECHNOLOGIES - validTechnologies.length);

    return [...validTechnologies, ...fillTechnologies];
  }, [recentTechnologyIds, technologies, removeRecentTechnology]);

  useEffect(() => {
    if (currentTechnologyId && technologies.some((tech) => tech.id === currentTechnologyId)) {
      addRecentTechnology(currentTechnologyId);
    }
  }, [currentTechnologyId, technologies, addRecentTechnology]);

  useEffect(() => {
    if (isMobile && open) {
      setOpen(false);
    }
  }, [isMobile, open, setOpen]);

  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;
  const isIntel = user?.profile?.role === UserProfileRole.INTELLIGENCE;
  const isAdmin = user?.profile?.role === UserProfileRole.ADMIN;
  const isCollapsedExpandable = state === 'collapsed' && !isMobile;
  const userHasValidAccess = Boolean(user?.hasValidAccess);
  const hasCompany = user?.companies?.[0];
  const showTechnologiesList = isInnovator && hasCompany && userHasValidAccess && technologies.length > 0;
  const showParentOrganizationMenu = isInnovator && isSelectedCompanyParentOrganization && userHasValidAccess;

  type MenuItem =
    | { icon: React.ElementType; label: string; path: string; type: 'link' }
    | { icon: React.ElementType; label: string; onClick: () => void; type: 'button' }
    | { type: 'separator' };

  const menuItems: MenuItem[] = [];

  const hasInsightsTool = user?.featureFlags?.includes(UserFeatureFlag.INSIGHTS_TOOL);
  const hasResearchTool = user?.featureFlags?.includes(UserFeatureFlag.RESEARCH_TOOL);
  const hasThreadsTool = user?.featureFlags?.includes(UserFeatureFlag.THREADS_TOOL);
  const hasOrgSwitcher = user?.featureFlags?.includes(UserFeatureFlag.ORG_SWITCHER);
  const hasMeiaStudioV1 = canAccessMeiaStudioV1(user);
  const hasMeiaCampaigns = canAccessMeiaCampaigns(user);

  if (isAdmin) {
    menuItems.push(
      { icon: Users2Icon, label: 'Users', path: '/admin/users', type: 'link' },
      { icon: Building, label: 'Organizations', path: '/admin/organizations', type: 'link' },
      { type: 'separator' },
      { icon: FolderTreeIcon, label: 'Categories', path: '/admin/categories', type: 'link' },
      { icon: GlobeIcon, label: 'Domains', path: '/admin/domains', type: 'link' },
      { icon: TargetIcon, label: 'Uses', path: '/admin/uses', type: 'link' },
      { type: 'separator' },
      { icon: SparklesIcon, label: 'LLM Prompts', path: '/admin/llm/prompts', type: 'link' },
      { icon: ServerCogIcon, label: 'LLM Logs', path: '/admin/llm', type: 'link' },
    );
  }

  if (isInnovator) {
    menuItems.push({ icon: House, label: 'Home', path: '/', type: 'link' });

    if (hasCompany && userHasValidAccess) {
      menuItems.push({ icon: Building, label: 'Organization', path: '/organization', type: 'link' });
      menuItems.push({ icon: Package, label: 'Products', path: '/products', type: 'link' });
      menuItems.push({ icon: CpuIcon, label: 'Technologies', path: '/technologies', type: 'link' });
    }

    if (showParentOrganizationMenu) {
      menuItems.push({ icon: FolderIcon, label: 'Projects', path: '/projects', type: 'link' });
      menuItems.push({ icon: BriefcaseIcon, label: 'Portfolio', path: '/portfolio', type: 'link' });
    }
  }

  if (isIntel) {
    menuItems.push({ icon: Building2Icon, label: 'Organizations', path: '/intel/organizations', type: 'link' });
    menuItems.push({ icon: CpuIcon, label: 'Technologies', path: '/intel/technologies', type: 'link' });
    menuItems.push({ icon: ListChecksIcon, label: 'Tech Challenges', path: '/tech-challenges', type: 'link' });
    if (hasMeiaStudioV1) {
      menuItems.push({ icon: RadarIcon, label: 'Studies', path: '/meia', type: 'link' });
    }
    if (hasMeiaCampaigns) {
      menuItems.push({ icon: FlagIcon, label: 'Campaigns', path: '/campaigns', type: 'link' });
    }
    if (hasCompany && userHasValidAccess) {
      menuItems.push({ icon: Building, label: 'My Organization', path: '/organization', type: 'link' });
    }
  }

  if (isExpert) {
    if (user.expertProfile) {
      menuItems.push({ icon: ListChecksIcon, label: 'Assessments', path: '/assessments', type: 'link' });
    }

    menuItems.push({ icon: UserIcon, label: 'Expert Profile', path: '/expert-profile', type: 'link' });
  }

  if (hasInsightsTool) {
    menuItems.push({ icon: LineChartIcon, label: 'Insights', path: '/insights', type: 'link' });
  }

  if (hasResearchTool) {
    menuItems.push({ icon: FileSearchIcon, label: 'Research', path: '/research', type: 'link' });
  }

  if (hasThreadsTool) {
    menuItems.push({ icon: MessageSquareIcon, label: 'Threads', path: '/threads', type: 'link' });
  }

  const handleSidebarClick = (event: React.MouseEvent) => {
    if (state === 'collapsed' && !isMobile) {
      const target = event.target as HTMLElement;
      const isMenuButton = target.closest('[data-sidebar="menu-button"]');
      const isTrigger = target.closest('[data-sidebar="trigger"]');
      const isFooter = target.closest('[data-sidebar="footer"]');

      if (!isMenuButton && !isTrigger && !isFooter) {
        setOpen(true);
      }
    }
  };

  return (
    <Sidebar
      collapsible="icon"
      data-admin-sidebar={isAdmin ? 'true' : undefined}
      onClick={handleSidebarClick}
      className={cn(isCollapsedExpandable && 'cursor-e-resize')}
    >
      <SidebarHeader className="relative py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Avatar className={cn('h-8 w-8', isCollapsedExpandable && 'group-hover:opacity-0 transition-opacity')}>
              <AvatarImage
                src={logoImg}
                className="h-8 w-8 object-contain"
              />
            </Avatar>
            {open && (
              <div className="ml-3">
                <img
                  alt="Azymmetric logo text"
                  className="h-full w-full"
                  src={logoText}
                />
              </div>
            )}
          </div>
          {!isMobile && open && (
            <SidebarTrigger
              tooltip="Collapse"
              className="size-8 p-2 hover:cursor-w-resize text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md transition-colors"
            >
              <span className="sr-only">Collapse</span>
            </SidebarTrigger>
          )}
        </div>
        {!isMobile && !open && (
          <div
            className={cn(
              'absolute top-4 left-1/2 -translate-x-1/2 transition-opacity',
              'opacity-0 group-hover:opacity-100',
            )}
          >
            <SidebarTrigger
              tooltip="Expand"
              className="size-8 p-2 hover:cursor-pointer text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md transition-colors"
            >
              <span className="sr-only">Expand</span>
            </SidebarTrigger>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className="py-0">
          <SidebarGroupContent>
            <SidebarMenu className={cn('gap-1', state === 'collapsed' && 'px-0.5')}>
              {menuItems.map((item, index) => {
                if (item.type === 'separator') {
                  return (
                    <div
                      key={`separator-${index}`}
                      className="my-2 mx-3 h-px bg-sidebar-border group-data-[collapsible=icon]:mx-1"
                    />
                  );
                }

                const { icon: Icon, label, type } = item;

                if (type === 'link') {
                  const isSelected = currentPath === item.path || currentPath.startsWith(`${item.path}/`);
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        asChild
                        isActive={isSelected}
                        tooltip={label}
                        className={cn(
                          'h-10 px-3 text-[15px] group-data-[collapsible=icon]:w-11 group-data-[collapsible=icon]:justify-center',
                          isSelected
                            ? 'bg-sidebar-accent/80 font-medium text-sidebar-accent-foreground'
                            : 'font-normal text-sidebar-foreground/80',
                        )}
                      >
                        <Link to={item.path}>
                          <Icon className="size-5" />
                          <span className="whitespace-nowrap group-data-[collapsible=icon]:hidden group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:w-0 transition-[opacity,width] group-data-[collapsible=icon]:duration-75">
                            {label}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <SidebarMenuItem key={label}>
                    <SidebarMenuButton
                      type="button"
                      tooltip={label}
                      className={cn(
                        'h-11 px-3 text-base group-data-[collapsible=icon]:justify-center',
                        'font-normal text-sidebar-foreground/80',
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                        item.onClick();
                      }}
                    >
                      <Icon className="size-5" />
                      <span className="whitespace-nowrap group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:hidden group-data-[collapsible=icon]:w-0 transition-[opacity,width] group-data-[collapsible=icon]:duration-75">
                        {label}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {showTechnologiesList && (
          <SidebarGroup className={cn('pt-2 pb-0', state === 'collapsed' && 'hidden')}>
            <SidebarGroupContent>
              <SidebarMenu>
                <Collapsible
                  open={!recentTechnologiesCollapsed}
                  onOpenChange={(isOpen) => setRecentTechnologiesCollapsed(!isOpen)}
                  className="group/collapsible"
                >
                  <SidebarMenuItem className="group/collapsible-item">
                    <SidebarGroupLabel
                      asChild
                      className="hover:text-sidebar-accent-foreground text-xs cursor-pointer"
                    >
                      <CollapsibleTrigger className="flex items-center w-full">
                        Recent Technologies
                        <ChevronRight className="ml-2 transition-transform opacity-100 group-hover/collapsible-item:opacity-100 group-data-[state=open]/collapsible:rotate-90 group-data-[state=open]/collapsible:opacity-0 group-data-[state=open]/collapsible:group-hover/collapsible-item:opacity-100" />
                      </CollapsibleTrigger>
                    </SidebarGroupLabel>
                    <CollapsibleContent>
                      <SidebarMenuSub className="mx-0 border-l-0 px-0 gap-1">
                        {recentTechnologies.map((tech) => {
                          const isActive = tech.id === currentTechnologyId;
                          return (
                            <SidebarMenuSubItem key={tech.id}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={isActive}
                                className={cn(
                                  'translate-x-0 font-normal py-2 pl-4 pr-2 text-xs group-data-[collapsible=icon]:justify-center',
                                  isActive
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                    : 'text-sidebar-foreground/80',
                                )}
                              >
                                <Link to={`/technology/${tech.id}`}>
                                  <span className="whitespace-nowrap group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:w-0 transition-[opacity,width] group-data-[collapsible=icon]:duration-75">
                                    {tech.name}
                                  </span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter
        className={cn('flex items-center justify-center p-2 rounded-none', state === 'collapsed' && 'p-0')}
      >
        {hasOrgSwitcher && isInnovator && hasCompany && userHasValidAccess && user?.companies && (
          <SidebarGroup className="p-0">
            <SidebarGroupContent>
              <OrganizationSwitcher companies={user.companies} />
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <div className="w-full flex py-3 px-1 hover:bg-sidebar-accent transition-colors rounded-md">
          <UserComponent />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};
